import React, { useState, useEffect } from 'react';
import { Play, Info, Star, Layers, ChevronRight, ChevronLeft } from 'lucide-react';
import { MediaItem } from '../types';
import { cleanGenres } from '../utils/genreUtils';

interface HeroBannerProps {
  items: MediaItem[];
  onPlay: (item: MediaItem, episodeIndex?: number) => void;
  onOpenDetails: (item: MediaItem) => void;
  onToggleWatchlist?: (item: MediaItem) => void;
  isInWatchlist?: (itemId: string) => boolean;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  items,
  onPlay,
  onOpenDetails
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto rotate featured items every 9 seconds
  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 9000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (!items || items.length === 0) return null;

  const currentItem = items[currentIndex] || items[0];
  const displayGenres = cleanGenres(currentItem.genres);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  return (
    <div id="hero-banner" className="relative w-full min-h-[38vh] sm:min-h-[44vh] md:min-h-[48vh] flex items-end pb-8 sm:pb-10 pt-4 sm:pt-6 overflow-hidden">
      {/* Dynamic Cinematic Backdrop */}
      <div className="absolute inset-0 z-0">
        <img
          src={currentItem.backdrop || currentItem.poster}
          alt={currentItem.title}
          className="w-full h-full object-cover object-center scale-105 transition-transform duration-700 ease-out"
          referrerPolicy="no-referrer"
        />
        {/* Layered Vignette and Gradient Fades */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#070b14] via-[#070b14]/65 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070b14] via-[#070b14]/85 to-transparent w-full md:w-3/4" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sky-500/10 via-transparent to-transparent" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-xl space-y-2.5">
          
          {/* Collection / Japanese Title Badge */}
          <div className="flex flex-wrap items-center gap-1.5">
            {currentItem.collectionName && (
              <span className="text-[11px] text-sky-300 font-bold px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-500/30 flex items-center gap-1">
                <Layers className="w-3 h-3" />
                <span>{currentItem.collectionName}</span>
              </span>
            )}
            {currentItem.originalTitle && (
              <span className="text-[11px] text-slate-300/90 font-jp px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                {currentItem.originalTitle}
              </span>
            )}
          </div>

          {/* Main Title */}
          <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-white tracking-tight leading-tight drop-shadow-md">
            {currentItem.title}
          </h1>

          {/* Meta Info Row */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300 font-medium">
            {currentItem.rating && (
              <div className="flex items-center gap-1 text-sky-400 bg-sky-500/10 px-1.5 py-0.5 rounded border border-sky-500/20">
                <Star className="w-3 h-3 fill-sky-400" />
                <span className="font-bold">{currentItem.rating.toFixed(1)}</span>
              </div>
            )}
            <span>{currentItem.year || '2024'}</span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 text-[10px] uppercase">
              {currentItem.type === 'series' 
                ? `${currentItem.numberOfEpisodes || currentItem.episodes?.length || 0} Episodis`
                : `${currentItem.runtime || 90} min`}
            </span>
            {displayGenres.length > 0 && (
              <>
                <span className="w-1 h-1 rounded-full bg-slate-600" />
                <div className="flex flex-wrap gap-1">
                  {displayGenres.slice(0, 3).map((g, idx) => (
                    <span key={idx} className="text-slate-400">
                      {g}{idx < Math.min(2, displayGenres.length - 1) ? ',' : ''}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Plot Summary */}
          <p className="text-xs sm:text-sm text-slate-300 line-clamp-2 md:line-clamp-3 leading-relaxed max-w-lg drop-shadow">
            {currentItem.summary}
          </p>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              id="hero-play-button"
              data-focusable="true"
              onClick={() => onPlay(currentItem)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs sm:text-sm shadow-lg shadow-sky-500/25 transition-all transform hover:scale-105 active:scale-95 focus:ring-2 focus:ring-sky-400"
            >
              <Play className="w-4 h-4 fill-white" />
              <span>Reproduir Ara</span>
            </button>

            <button
              id="hero-details-button"
              data-focusable="true"
              onClick={() => onOpenDetails(currentItem)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs sm:text-sm backdrop-blur-md border border-white/15 transition-all transform hover:scale-105 active:scale-95 focus:ring-2 focus:ring-white/50"
            >
              <Info className="w-4 h-4" />
              <span>Més Informació</span>
            </button>
          </div>

        </div>
      </div>

      {/* Carousel Navigation Indicators */}
      {items.length > 1 && (
        <div className="absolute right-4 sm:right-8 bottom-6 z-20 flex items-center gap-2">
          <button
            onClick={handlePrev}
            className="p-1.5 rounded-full bg-black/40 hover:bg-black/80 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1">
            {items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  currentIndex === idx ? 'w-6 bg-sky-400' : 'w-1.5 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleNext}
            className="p-1.5 rounded-full bg-black/40 hover:bg-black/80 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Següent"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
